/**
 * Citrus Trees Data for Blog Content Generator
 * Why this matters: Preloaded data for all 40 citrus trees with UCR links and SEO titles
 * Source: CSV from keywords folder + SEO titles from us-citrus-markdowns
 */

export interface CitrusTree {
  id: string;
  treeName: string;
  citrusVarietyLink: string;
  seoTitle: string;
  h1Title: string;
  category: string;
}

/**
 * All 40 Citrus Trees from UCR Citrus Variety Collection
 * Why this matters: Provides comprehensive data for generating rich, AEO-optimized content
 */
export const CITRUS_TREES: CitrusTree[] = [
  // LEMONS
  {
    id: '1',
    treeName: 'Meyer Lemon Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3737',
    seoTitle: 'Meyer Valley Lemon Tree: America\'s Sweet Backyard Favorite with Texas Roots',
    h1Title: 'Meyer Valley Lemon Tree: America\'s Sweet Backyard Favorite with Texas Roots',
    category: 'Lemons'
  },
  {
    id: '2',
    treeName: 'Eureka Lemon Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc4094',
    seoTitle: 'Eureka Lemon Tree, the Classic Zesty Lemon Loved in Kitchens Everywhere',
    h1Title: 'Eureka Lemon Tree: The Classic Zesty Lemon Loved in Kitchens Everywhere',
    category: 'Lemons'
  },
  {
    id: '3',
    treeName: 'Pink Variegated Lemon Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc599',
    seoTitle: 'Pink Variegated Lemon Tree, the Stunning Pink-Fleshed Citrus California Made Famous',
    h1Title: 'Pink Variegated Lemon Tree: The Stunning Pink-Fleshed Citrus California Made Famous',
    category: 'Lemons'
  },
  
  // LIMES
  {
    id: '4',
    treeName: 'Persian Lime Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3772',
    seoTitle: 'Persian (Tahiti) Lime Tree, the Seedless Bar Lime Chefs Depend On',
    h1Title: 'Persian (Tahiti) Lime Tree: The Seedless Bar Lime Chefs Depend On',
    category: 'Limes'
  },
  {
    id: '5',
    treeName: 'Thornless Mexican Key Lime Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc2683',
    seoTitle: 'Mexican Key Lime Tree Thornless, the True Margarita Lime of Mexico and the Caribbean',
    h1Title: 'Mexican Key Lime Tree (Thornless): The True Margarita Lime of Mexico and the Caribbean',
    category: 'Limes'
  },
  {
    id: '6',
    treeName: 'Kaffir / Makrut Lime Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc2454',
    seoTitle: 'Makrut Lime Tree, the Legendary Thai Citrus for Powerful Aromatic Leaves and Oils',
    h1Title: 'Makrut Lime Tree: The Legendary Thai Citrus for Powerful Aromatic Leaves and Oils',
    category: 'Limes'
  },
  {
    id: '7',
    treeName: 'Rangpur Lime Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc2318',
    seoTitle: 'Rangpur Lime Tree, the Fiery Indian Mandarin-Lime Hybrid Bursting with Flavor',
    h1Title: 'Rangpur Lime Tree: The Fiery Indian Mandarin-Lime Hybrid Bursting with Flavor',
    category: 'Limes'
  },
  
  // SWEET ORANGES
  {
    id: '8',
    treeName: 'Valencia Orange Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc2750',
    seoTitle: 'Valencia Orange Tree, the World\'s #1 Juicing Orange with Deep Spanish Heritage',
    h1Title: 'Valencia Orange Tree: The World\'s #1 Juicing Orange with Deep Spanish Heritage',
    category: 'Sweet Oranges'
  },
  {
    id: '9',
    treeName: 'Marrs Orange Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3865',
    seoTitle: 'Marrs Sweet Orange Tree, the Texas Original Discovered Just South of Donna',
    h1Title: 'Marrs Sweet Orange Tree: The Texas Original Discovered Just South of Donna',
    category: 'Sweet Oranges'
  },
  {
    id: '10',
    treeName: 'Pineapple Orange Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3858',
    seoTitle: 'Pineapple Orange Tree, Florida\'s Tropical-Scented Sweet Orange Treasure',
    h1Title: 'Pineapple Orange Tree: Florida\'s Tropical-Scented Sweet Orange Treasure',
    category: 'Sweet Oranges'
  },
  
  // NAVELS
  {
    id: '11',
    treeName: 'Washington Navel Orange Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc1241A',
    seoTitle: 'Washington Navel Orange Tree, the Historic Seedless Orange That Built California\'s Citrus Empire',
    h1Title: 'Washington Navel Orange Tree: The Historic Seedless Orange That Built California\'s Citrus Empire',
    category: 'Navels'
  },
  {
    id: '12',
    treeName: 'Cara Cara Navel Orange Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3994',
    seoTitle: 'Cara Cara Pink Navel Tree, the Sweet Seedless Citrus Born from a Venezuelan Miracle',
    h1Title: 'Cara Cara Pink Navel Tree: The Sweet Seedless Citrus Born from a Venezuelan Miracle',
    category: 'Navels'
  },
  {
    id: '13',
    treeName: 'N-33 Navel Orange Tree',
    citrusVarietyLink: 'none - use internet',
    seoTitle: 'N-33 Texas Navel Tree, the Rare Donna, Texas Mutation with Unmatched Sweetness',
    h1Title: 'N-33 Texas Navel Tree: The Rare Donna, Texas Mutation with Unmatched Sweetness',
    category: 'Navels'
  },
  
  // BLOOD ORANGES
  {
    id: '14',
    treeName: 'Sanguinelli Blood Orange Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3828',
    seoTitle: 'Sanguinelli Blood Orange Tree, the Bold Spanish Crimson Citrus with Intense Sweetness',
    h1Title: 'Sanguinelli Blood Orange Tree: The Bold Spanish Crimson Citrus with Intense Sweetness',
    category: 'Blood Oranges'
  },
  {
    id: '15',
    treeName: 'Moro Blood Orange Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3811',
    seoTitle: 'Moro Blood Orange Tree, the Deep Sicilian Red Orange Packed with Wild Berry Flavor',
    h1Title: 'Moro Blood Orange Tree: The Deep Sicilian Red Orange Packed with Wild Berry Flavor',
    category: 'Blood Oranges'
  },
  {
    id: '16',
    treeName: 'Tarocco Blood Orange Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3596',
    seoTitle: 'Tarocco Blood Orange Tree, Italy\'s Beloved Sweet Blood Orange with Ancient Roots',
    h1Title: 'Tarocco Blood Orange Tree: Italy\'s Beloved Sweet Blood Orange with Ancient Roots',
    category: 'Blood Oranges'
  },
  
  // GRAPEFRUITS & HYBRIDS
  {
    id: '17',
    treeName: 'Rio Red Grapefruit Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3916',
    seoTitle: 'Rio Red Grapefruit Tree, the Texas Legend Dr. Skaria Helped Make the State Fruit',
    h1Title: 'Rio Red Grapefruit Tree: The Texas Legend Dr. Skaria Helped Make the State Fruit',
    category: 'Grapefruits'
  },
  {
    id: '18',
    treeName: 'Duncan Grapefruit Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3832',
    seoTitle: 'Duncan Grapefruit Tree, Florida\'s Original Juicy Heirloom Grapefruit',
    h1Title: 'Duncan Grapefruit Tree: Florida\'s Original Juicy Heirloom Grapefruit',
    category: 'Grapefruits'
  },
  {
    id: '19',
    treeName: 'Oro Blanco Grapefruit Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3602',
    seoTitle: 'Oro Blanco Sweet Grapefruit Tree, the Low-Acid UCR Hybrid Everyone Loves',
    h1Title: 'Oro Blanco Sweet Grapefruit Tree: The Low-Acid UCR Hybrid Everyone Loves',
    category: 'Grapefruits'
  },
  
  // POMELOS
  {
    id: '20',
    treeName: 'Chandler Pomelo Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3244',
    seoTitle: 'Chandler Pomelo Tree, the Giant Pink UCR Pomelo with Ancient Citrus Lineage',
    h1Title: 'Chandler Pomelo Tree: The Giant Pink UCR Pomelo with Ancient Citrus Lineage',
    category: 'Pomelos'
  },
  {
    id: '21',
    treeName: 'Hirado Buntan Pomelo Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc4122',
    seoTitle: 'Hirado Buntan Pomelo Tree, the Elegant Japanese Pomelo with Floral Sweetness',
    h1Title: 'Hirado Buntan Pomelo Tree: The Elegant Japanese Pomelo with Floral Sweetness',
    category: 'Pomelos'
  },
  
  // MANDARINS, SATSUMAS, TANGELOS
  {
    id: '22',
    treeName: 'Shiranui (Dekopon) Mandarin Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc4249',
    seoTitle: 'Shiranui (Dekopon) Mandarin Tree, the Famous Japanese "Sumo" Citrus with Explosive Sweetness',
    h1Title: 'Shiranui (Dekopon) Mandarin Tree: The Famous Japanese "Sumo" Citrus with Explosive Sweetness',
    category: 'Mandarins'
  },
  {
    id: '23',
    treeName: 'Page Mandarin Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3616',
    seoTitle: 'Page Mandarin Tree, the California Tangelo Hybrid Chefs Call a Flavor Bomb',
    h1Title: 'Page Mandarin Tree: The California Tangelo Hybrid Chefs Call a Flavor Bomb',
    category: 'Mandarins'
  },
  {
    id: '24',
    treeName: 'Ponkan Mandarin Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3849',
    seoTitle: 'Ponkan Mandarin Tree, the Ancient Chinese Honey Mandarin Loved Worldwide',
    h1Title: 'Ponkan Mandarin Tree: The Ancient Chinese Honey Mandarin Loved Worldwide',
    category: 'Mandarins'
  },
  {
    id: '25',
    treeName: 'Murcott (Afourer) Mandarin Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3953',
    seoTitle: 'Murcott Honey Mandarin Tree, the Sweet Tangerine with Moroccan and Florida History',
    h1Title: 'Murcott Honey Mandarin Tree: The Sweet Tangerine with Moroccan and Florida History',
    category: 'Mandarins'
  },
  {
    id: '26',
    treeName: 'Frost Owari Satsuma Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3178',
    seoTitle: 'Frost Owari Satsuma Tree, the Cold-Hardy Japanese Classic That Never Misses a Harvest',
    h1Title: 'Frost Owari Satsuma Tree: The Cold-Hardy Japanese Classic That Never Misses a Harvest',
    category: 'Satsumas'
  },
  {
    id: '27',
    treeName: 'Dobashi Beni Satsuma Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3794',
    seoTitle: 'Dobashi Beni Satsuma Tree, the Deep-Orange Japanese Satsuma with Rich, Sweet Flavor',
    h1Title: 'Dobashi Beni Satsuma Tree: The Deep-Orange Japanese Satsuma with Rich, Sweet Flavor',
    category: 'Satsumas'
  },
  {
    id: '28',
    treeName: 'Cleopatra Mandarin Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3844',
    seoTitle: 'Cleopatra Mandarin Tree, the Ancient Indian Citrus with Global Influence',
    h1Title: 'Cleopatra Mandarin Tree: The Ancient Indian Citrus with Global Influence',
    category: 'Mandarins'
  },
  
  // HYBRIDS & SPECIALTY CITRUS
  {
    id: '29',
    treeName: 'Nippon Orangequat Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3360',
    seoTitle: 'Nippon Orangequat Tree, the Rare Citrus Fusion That Thrills Collectors',
    h1Title: 'Nippon Orangequat Tree: The Rare Citrus Fusion That Thrills Collectors',
    category: 'Hybrids'
  },
  {
    id: '30',
    treeName: 'New Zealand Lemonade Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc4181',
    seoTitle: 'New Zealand Lemonade Tree, the Naturally Sweet Citrus You Can Drink Right Off the Tree',
    h1Title: 'New Zealand Lemonade Tree: The Naturally Sweet Citrus You Can Drink Right Off the Tree',
    category: 'Hybrids'
  },
  {
    id: '31',
    treeName: 'Etrog Citron Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3891',
    seoTitle: 'Etrog Citron Tree, the Sacred Biblical Citrus with Thousands of Years of History',
    h1Title: 'Etrog Citron Tree: The Sacred Biblical Citrus with Thousands of Years of History',
    category: 'Specialty'
  },
  
  // EXOTIC CITRUS
  {
    id: '32',
    treeName: 'Yuzu Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc1216',
    seoTitle: 'Yuzu Tree, Japan\'s Legendary Aromatic Citrus That Chefs Treat Like Gold',
    h1Title: 'Yuzu Tree: Japan\'s Legendary Aromatic Citrus That Chefs Treat Like Gold',
    category: 'Exotic'
  },
  {
    id: '33',
    treeName: 'Australian Finger Lime Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3661',
    seoTitle: 'Australian Finger Lime Tree, the Rainforest Citrus Caviar That Commands Top Dollar',
    h1Title: 'Australian Finger Lime Tree: The Rainforest Citrus Caviar That Commands Top Dollar',
    category: 'Exotic'
  },
  
  // KUMQUATS
  {
    id: '34',
    treeName: 'Nagami Kumquat Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc132',
    seoTitle: 'Nagami Kumquat Tree, the Sweet-Peel Tart-Burst Citrus from Ancient China',
    h1Title: 'Nagami Kumquat Tree: The Sweet-Peel Tart-Burst Citrus from Ancient China',
    category: 'Kumquats'
  },
  {
    id: '35',
    treeName: 'Meiwa Kumquat Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc1471',
    seoTitle: 'Meiwa Kumquat Tree, the Sweet Round Japanese Kumquat Perfect for Snacking',
    h1Title: 'Meiwa Kumquat Tree: The Sweet Round Japanese Kumquat Perfect for Snacking',
    category: 'Kumquats'
  },
  {
    id: '36',
    treeName: 'Fukushu Kumquat Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3475',
    seoTitle: 'Fukushu Kumquat Tree, the Large Round Citrus with Sweet Orange-Like Peel',
    h1Title: 'Fukushu Kumquat Tree: The Large Round Citrus with Sweet Orange-Like Peel',
    category: 'Kumquats'
  },
  
  // LIMEQUATS & OTHER
  {
    id: '37',
    treeName: 'Eustis Limequat Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3603',
    seoTitle: 'Eustis Limequat Tree, the Florida-Born Lime-Kumquat Hybrid with Edible Sweet Peel',
    h1Title: 'Eustis Limequat Tree: The Florida-Born Lime-Kumquat Hybrid with Edible Sweet Peel',
    category: 'Hybrids'
  },
  {
    id: '38',
    treeName: 'Palestine Sweet Lime Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc1482',
    seoTitle: 'Palestine Sweet Lime Tree, the Gentle Middle Eastern Citrus with Ancient Medicinal Roots',
    h1Title: 'Palestine Sweet Lime Tree: The Gentle Middle Eastern Citrus with Ancient Medicinal Roots',
    category: 'Limes'
  },
  {
    id: '39',
    treeName: 'Calamondin / Calamansi Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc2592',
    seoTitle: 'Calamondin (Calamansi) Tree, the Iconic Filipino Citrus for Drinks, Sauces, and Flavor Punch',
    h1Title: 'Calamondin (Calamansi) Tree: The Iconic Filipino Citrus for Drinks, Sauces, and Flavor Punch',
    category: 'Exotic'
  },
  {
    id: '40',
    treeName: 'Kishu Mandarin Tree',
    citrusVarietyLink: 'https://citrusvariety.ucr.edu/crc3906',
    seoTitle: 'Kishu Mandarin Tree, Japan\'s Bite-Sized Seedless Mandarin Everyone Falls in Love With',
    h1Title: 'Kishu Mandarin Tree: Japan\'s Bite-Sized Seedless Mandarin Everyone Falls in Love With',
    category: 'Mandarins'
  },
  {
    id: '41',
    treeName: 'Dancy Mandarin Tree',
    citrusVarietyLink: 'none - use internet',
    seoTitle: "Dancy Mandarin Tree, America's Original Christmas Tangerine with Bold Old-Fashioned Sweetness",
    h1Title: "Dancy Mandarin Tree: America's Original Christmas Tangerine with Bold Old-Fashioned Sweetness",
    category: 'Mandarins'
  }
];

/**
 * Get tree by name
 * Why this matters: Quick lookup for SEO data when generating content
 */
export const getTreeByName = (treeName: string): CitrusTree | undefined => {
  return CITRUS_TREES.find(tree => tree.treeName === treeName);
};

/**
 * Get trees by category
 * Why this matters: Allows filtering for batch operations
 */
export const getTreesByCategory = (category: string): CitrusTree[] => {
  return CITRUS_TREES.filter(tree => tree.category === category);
};

/**
 * Get all unique categories
 * Why this matters: For UI filtering options
 */
export const getCategories = (): string[] => {
  const categories: string[] = [];
  CITRUS_TREES.forEach(tree => {
    if (!categories.includes(tree.category)) {
      categories.push(tree.category);
    }
  });
  return categories;
};

/**
 * Check if URL is valid (not "none - use internet")
 * Why this matters: Some trees don't have UCR links and need internet research only
 */
export const hasValidUcrLink = (citrusVarietyLink: string): boolean => {
  return citrusVarietyLink.startsWith('https://');
};
